/**
 * StoreCascadeFilter — barra filtri Brand → Area → Negozi in stile Dashboard Area
 * (Select per brand/area, Popover multi-select con ricerca per i negozi).
 * Il parent (brand) limita i child (aree, negozi), con pruning delle selezioni
 * figlie non più valide.
 */
import React, { useMemo, useState } from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import { Store as StoreIcon, X, FilterX } from 'lucide-react';

const areaOf = (s) => s.area || '';

export const emptyCascade = () => ({ brand: 'all', area: 'all', storeIds: [] });

// Set di store_id ammessi dal filtro corrente, o null se nessun filtro attivo.
export function computeAllowedStoreIds(stores, sel) {
  if (sel.brand === 'all' && sel.area === 'all' && sel.storeIds.length === 0) return null;
  let pool = stores;
  if (sel.brand !== 'all') pool = pool.filter(s => (s.brand_id || '') === sel.brand);
  if (sel.area !== 'all') pool = pool.filter(s => areaOf(s) === sel.area);
  if (sel.storeIds.length > 0) {
    const chosen = new Set(sel.storeIds);
    pool = pool.filter(s => chosen.has(s.store_id));
  }
  return new Set(pool.map(s => s.store_id));
}

export const StoreCascadeFilter = ({ stores, sel, onChange, testPrefix = 'cascade' }) => {
  const [storesOpen, setStoresOpen] = useState(false);
  const [storeQuery, setStoreQuery] = useState('');

  const brands = useMemo(() => {
    const map = new Map();
    for (const s of stores) if (s.brand_id) map.set(s.brand_id, s.brand_name || s.brand_id);
    return [...map.entries()].sort((a, b) => a[1].localeCompare(b[1], 'it'));
  }, [stores]);

  // Aree filtrate dal brand selezionato (cascata parent → child).
  const brandPool = useMemo(
    () => sel.brand !== 'all' ? stores.filter(s => (s.brand_id || '') === sel.brand) : stores,
    [stores, sel.brand]);
  const areas = useMemo(
    () => [...new Set(brandPool.map(areaOf).filter(Boolean))].sort((a, b) => a.localeCompare(b, 'it')),
    [brandPool]);

  // Negozi filtrati da brand + area.
  const availableStores = useMemo(() => {
    const pool = sel.area !== 'all' ? brandPool.filter(s => areaOf(s) === sel.area) : brandPool;
    return [...pool].sort((a, b) => (a.display_name || a.name || '').localeCompare(b.display_name || b.name || '', 'it', { sensitivity: 'base' }));
  }, [brandPool, sel.area]);

  const pruneStores = (pool, storeIds) => {
    const valid = new Set(pool.map(s => s.store_id));
    return storeIds.filter(id => valid.has(id));
  };

  const setBrand = (brand) => {
    const pool = brand !== 'all' ? stores.filter(s => (s.brand_id || '') === brand) : stores;
    const validAreas = new Set(pool.map(areaOf));
    const area = sel.area !== 'all' && validAreas.has(sel.area) ? sel.area : 'all';
    const pool2 = area !== 'all' ? pool.filter(s => areaOf(s) === area) : pool;
    onChange({ brand, area, storeIds: pruneStores(pool2, sel.storeIds) });
  };

  const setArea = (area) => {
    const pool = area !== 'all' ? brandPool.filter(s => areaOf(s) === area) : brandPool;
    onChange({ ...sel, area, storeIds: pruneStores(pool, sel.storeIds) });
  };

  const toggleStore = (id, checked) => {
    onChange({ ...sel, storeIds: checked ? [...sel.storeIds, id] : sel.storeIds.filter(x => x !== id) });
  };

  const hasFilter = sel.brand !== 'all' || sel.area !== 'all' || sel.storeIds.length > 0;

  return (
    <div className="bg-white border border-slate-200 px-4 py-3 flex flex-wrap items-center gap-3" data-testid={`${testPrefix}-filter`}>
      <span className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500 mr-1">Filtri</span>
      <Select value={sel.brand} onValueChange={setBrand}>
        <SelectTrigger className="w-44 rounded-none" data-testid={`${testPrefix}-brand-filter`}><SelectValue placeholder="Brand" /></SelectTrigger>
        <SelectContent>
          <SelectItem value="all">Tutti i brand</SelectItem>
          {brands.map(([id, name]) => <SelectItem key={id} value={id}>{name}</SelectItem>)}
        </SelectContent>
      </Select>
      <Select value={sel.area} onValueChange={setArea}>
        <SelectTrigger className="w-40 rounded-none" data-testid={`${testPrefix}-area-filter`}><SelectValue placeholder="Area" /></SelectTrigger>
        <SelectContent>
          <SelectItem value="all">Tutte le aree</SelectItem>
          {areas.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}
        </SelectContent>
      </Select>
      <Popover open={storesOpen} onOpenChange={setStoresOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className="rounded-none h-9 px-3 justify-between font-normal min-w-[12rem]"
            data-testid={`${testPrefix}-stores-trigger`}
          >
            <span className="flex items-center gap-2 text-sm truncate">
              <StoreIcon className="w-4 h-4 text-slate-500" />
              {sel.storeIds.length === 0
                ? `Tutti i negozi (${availableStores.length})`
                : `${sel.storeIds.length} negozi selezionati`}
            </span>
            {sel.storeIds.length > 0 && (
              <X
                className="w-3.5 h-3.5 text-slate-500 hover:text-slate-900 ml-2"
                data-testid={`${testPrefix}-stores-clear`}
                onClick={(e) => { e.stopPropagation(); onChange({ ...sel, storeIds: [] }); }}
              />
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-72 p-0 rounded-none" align="start">
          <div className="p-2 border-b border-slate-100">
            <input
              type="text"
              value={storeQuery}
              onChange={(e) => setStoreQuery(e.target.value)}
              placeholder="Cerca negozio..."
              className="w-full px-2 py-1.5 text-sm border border-slate-200 rounded-none focus:outline-none focus:border-slate-900"
              data-testid={`${testPrefix}-stores-search`}
            />
            <div className="flex justify-between mt-2 text-xs">
              <button
                type="button"
                className="text-slate-600 hover:text-slate-900 underline"
                data-testid={`${testPrefix}-stores-select-all`}
                onClick={() => onChange({ ...sel, storeIds: availableStores.map(s => s.store_id) })}
              >Seleziona tutti</button>
              <button
                type="button"
                className="text-slate-600 hover:text-slate-900 underline"
                data-testid={`${testPrefix}-stores-deselect-all`}
                onClick={() => onChange({ ...sel, storeIds: [] })}
              >Deseleziona tutti</button>
            </div>
          </div>
          <div className="max-h-72 overflow-y-auto py-1">
            {availableStores.length === 0 && (
              <div className="px-3 py-4 text-xs text-slate-500">Nessun negozio nel contesto.</div>
            )}
            {availableStores
              .filter(s => !storeQuery || (s.display_name || s.name || '').toLowerCase().includes(storeQuery.toLowerCase()))
              .map(s => (
                <label
                  key={s.store_id}
                  className="flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-slate-50 cursor-pointer"
                  data-testid={`${testPrefix}-stores-item-${s.store_id}`}
                >
                  <Checkbox
                    checked={sel.storeIds.includes(s.store_id)}
                    onCheckedChange={(v) => toggleStore(s.store_id, !!v)}
                  />
                  <span className="truncate">{s.display_name || s.name}</span>
                </label>
              ))}
          </div>
        </PopoverContent>
      </Popover>
      {hasFilter && (
        <button
          type="button"
          onClick={() => onChange(emptyCascade())}
          className="inline-flex items-center gap-1 text-xs text-slate-600 hover:text-slate-900 underline"
          data-testid={`${testPrefix}-clear-filters`}
        >
          <FilterX className="w-3.5 h-3.5" /> Azzera filtri
        </button>
      )}
    </div>
  );
};
